import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Calendar,
  Clock,
  ArrowLeft,
  Share2,
  Zap,
  User,
  ArrowUpRight,
  Twitter,
  Linkedin,
  Mic,
  Workflow,
} from 'lucide-react';
import { blogPosts } from '../data/blogData';
import { RevealSection, TextReveal, RevealCard } from '../components/Animations';

import SEO from '../components/SEO';

const BlogPostPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post) navigate('/blog');
  }, [post, navigate]);

  if (!post) return null;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://emergeautomations.com/blog/${id}`,
    },
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'Emerge Automations',
      url: 'https://emergeautomations.com',
    },
    publisher: {
      '@type': 'ProfessionalService',
      name: 'Emerge Automations',
      logo: {
        '@type': 'ImageObject',
        url: 'https://emergeautomations.com/logo.png',
      },
    },
  };

  const iconBtn =
    'w-10 h-10 rounded-[var(--radius-sm)] border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/25 transition-colors';

  return (
    <div className="relative">
      <SEO
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        ogType="article"
        ogImage={post.image}
        canonical={`https://emergeautomations.com/blog/${id}/`}
        schemaData={articleSchema}
        keywords={post.keywords || (post.tags ? post.tags.join(', ') : 'AI automation, business automation, AI insights')}
        faq={post.faq}
        breadcrumbs={[
          { name: 'Home', item: 'https://emergeautomations.com' },
          { name: 'Blog', item: 'https://emergeautomations.com/blog' },
          { name: post.title, item: `https://emergeautomations.com/blog/${id}` },
        ]}
      />

      <div className="pt-32 md:pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            {/* Back */}
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-10"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
              Back to insights
            </Link>

            {/* Meta */}
            <div className="flex items-center gap-5 mb-8 flex-wrap">
              <span className="eyebrow eyebrow--accent">{post.category}</span>
              <span className="w-1 h-1 rounded-full bg-zinc-600" />
              <span className="flex items-center gap-1.5 text-xs text-zinc-500 font-medium">
                <Calendar className="w-3.5 h-3.5" strokeWidth={1.75} />
                {post.date}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-zinc-500 font-medium">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.75} />
                {post.readTime}
              </span>
            </div>

            {/* Title */}
            <TextReveal
              as="h1"
              className="text-[clamp(2.25rem,5.5vw,4rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.035em] mb-10"
            >
              {post.title}
            </TextReveal>

            {/* Author / share */}
            <div className="flex items-center justify-between py-6 border-y border-white/10 mb-12">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full border border-white/10 bg-[color:var(--color-surface-1)] flex items-center justify-center">
                  <User className="w-5 h-5 text-[color:var(--color-accent)]" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Emerge Editorial Team</p>
                  <p className="eyebrow">AI Strategy Specialists</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    try {
                      const url = window.location.href;
                      const text = `Check out this insight from Emerge Automations: ${post.title}`;
                      window.open(
                        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
                        '_blank'
                      );
                    } catch {}
                  }}
                  className={iconBtn}
                  aria-label="Share on Twitter"
                  title="Share on Twitter"
                >
                  <Twitter className="w-4 h-4" strokeWidth={1.75} />
                </button>
                <button
                  onClick={() => {
                    try {
                      const url = window.location.href;
                      window.open(
                        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
                        '_blank'
                      );
                    } catch {}
                  }}
                  className={iconBtn}
                  aria-label="Share on LinkedIn"
                  title="Share on LinkedIn"
                >
                  <Linkedin className="w-4 h-4" strokeWidth={1.75} />
                </button>
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(window.location.href);
                      const btn = document.getElementById('copy-btn');
                      if (btn) {
                        const originalContent = btn.innerHTML;
                        btn.innerHTML = '<span class="text-[11px] font-medium text-[color:var(--color-accent)]">Copied</span>';
                        setTimeout(() => {
                          btn.innerHTML = originalContent;
                        }, 1800);
                      }
                    } catch {}
                  }}
                  id="copy-btn"
                  className={iconBtn}
                  aria-label="Copy article link"
                  title="Copy Link"
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
            </div>

            {/* Featured image */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="aspect-[16/9] rounded-[var(--radius-xl)] overflow-hidden mb-14 border border-white/10"
            >
              <img
                src={post.image}
                sizes="100vw"
                alt={`Emerge Automations Insight: ${post.title} - AI Strategy and Automation for Businesses`}
                className="w-full h-full object-cover"
                width="1200"
                height="675"
                referrerPolicy="no-referrer"
                fetchPriority="high"
                decoding="async"
              />
            </motion.div>

            {/* Content */}
            <RevealSection>
              <article className="mb-24">
                {post.longContent ? (
                  <div
                    className="prose-emerge max-w-none text-[17px] leading-[1.8]
                      [&_h2]:text-2xl md:[&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:mt-14 [&_h2]:mb-5 [&_h2]:text-white [&_h2]:tracking-[-0.02em]
                      [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-white
                      [&_p]:mb-6
                      [&_ul]:mb-6 [&_ul]:pl-5 [&_ul]:list-disc
                      [&_ol]:mb-6 [&_ol]:pl-5 [&_ol]:list-decimal
                      [&_li]:mb-2"
                    dangerouslySetInnerHTML={{ __html: post.longContent }}
                  />
                ) : (
                  <>
                    <p className="text-xl text-zinc-300 leading-relaxed mb-12 font-medium">
                      {post.excerpt}
                    </p>

                    {post.content &&
                      post.content.map((section, idx) => {
                        if (section.type === 'heading') {
                          return (
                            <h2
                              key={idx}
                              className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[-0.02em] mt-14 mb-5"
                            >
                              {section.text}
                            </h2>
                          );
                        }
                        if (section.type === 'paragraph') {
                          return (
                            <p key={idx} className="text-[17px] text-zinc-300 leading-[1.8] mb-6">
                              {section.text}
                            </p>
                          );
                        }
                        if (section.type === 'quote') {
                          return (
                            <div
                              key={idx}
                              className="relative p-6 md:p-8 rounded-[var(--radius-lg)] border border-white/10 bg-[color:var(--color-surface-1)] mb-10 overflow-hidden"
                            >
                              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[color:var(--color-accent)]" />
                              <div className="flex items-center gap-2 mb-3">
                                <Zap className="w-4 h-4 text-[color:var(--color-accent)]" strokeWidth={1.75} />
                                <span className="eyebrow eyebrow--accent">Key insight</span>
                              </div>
                              <p className="text-lg text-zinc-200 leading-relaxed italic">
                                "{section.text}"
                              </p>
                            </div>
                          );
                        }
                        if (section.type === 'list') {
                          return (
                            <ul key={idx} className="space-y-3 mb-10 pl-1">
                              {section.items?.map((item, i) => (
                                <li
                                  key={i}
                                  className="flex items-start gap-3 text-[17px] text-zinc-300 leading-[1.7]"
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-accent)] mt-[0.7em] shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        return null;
                      })}
                  </>
                )}
              </article>
            </RevealSection>

            {/* Related Services */}
            <RevealSection className="mb-24">
              <div className="mb-8">
                <span className="eyebrow">Related services</span>
                <div className="mt-3 mb-5 h-px bg-white/10 w-full" />
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[-0.02em]">
                  Put this to work
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/services/ai-voice-agents/" className="group block">
                  <RevealCard className="h-full p-6 rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors">
                    <div className="w-10 h-10 rounded-[var(--radius-sm)] border border-white/10 bg-white/[0.02] flex items-center justify-center mb-5">
                      <Mic className="w-5 h-5 text-[color:var(--color-accent)]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[color:var(--color-accent)] transition-colors">
                      AI Voice Agents
                    </h3>
                    <p className="text-[14px] text-zinc-400 leading-relaxed mb-5">
                      Scale your customer support with low-latency AI voice technology.
                    </p>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                      Learn more
                      <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] transition-colors" strokeWidth={1.75} />
                    </div>
                  </RevealCard>
                </Link>
                <Link to="/services/n8n-workflow-automation/" className="group block">
                  <RevealCard className="h-full p-6 rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors">
                    <div className="w-10 h-10 rounded-[var(--radius-sm)] border border-white/10 bg-white/[0.02] flex items-center justify-center mb-5">
                      <Workflow className="w-5 h-5 text-[color:var(--color-accent)]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[color:var(--color-accent)] transition-colors">
                      Workflow Automation
                    </h3>
                    <p className="text-[14px] text-zinc-400 leading-relaxed mb-5">
                      Connect your entire tech stack with custom n8n workflows.
                    </p>
                    <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                      Learn more
                      <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] transition-colors" strokeWidth={1.75} />
                    </div>
                  </RevealCard>
                </Link>
              </div>
            </RevealSection>

            {/* CTA */}
            <RevealSection>
              <RevealCard className="relative p-10 md:p-14 rounded-[var(--radius-xl)] border border-white/10 bg-[color:var(--color-surface-1)] overflow-hidden text-center">
                <div
                  className="pointer-events-none absolute inset-0 opacity-60"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 120%, rgba(198,242,78,0.10), transparent 55%)',
                  }}
                />
                <div className="relative">
                  <span className="eyebrow eyebrow--accent mb-5 inline-block">Take action</span>
                  <h3 className="text-3xl md:text-4xl font-display font-semibold text-white leading-[1.1] tracking-[-0.025em] mb-4">
                    Ready to emerge?
                  </h3>
                  <p className="text-[16px] text-zinc-400 leading-relaxed max-w-xl mx-auto mb-8">
                    Don't let your business fall behind. Book a free 15-minute AI strategy call today and discover how we can automate your growth.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Link to="/#calendly-booking" className="btn-primary justify-center">
                      Book strategy call
                      <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                    <Link to="/blog/" className="btn-secondary justify-center">
                      More insights
                    </Link>
                  </div>
                </div>
              </RevealCard>
            </RevealSection>

            {/* Related posts */}
            <RevealSection className="mt-24">
              <div className="flex items-end justify-between gap-6 mb-8">
                <div>
                  <span className="eyebrow">Related insights</span>
                  <div className="mt-3 mb-5 h-px bg-white/10 w-full" />
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[-0.02em]">
                    Keep reading
                  </h2>
                </div>
                <Link
                  to="/blog/"
                  className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-[color:var(--color-accent)] transition-colors"
                >
                  View all
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </Link>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {blogPosts
                  .filter((p) => p.id !== id)
                  .slice(0, 2)
                  .map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.id}/`}
                      className="group block"
                      aria-label={`Read blog post: ${post.title}`}
                    >
                      <RevealCard className="h-full rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors overflow-hidden flex flex-col">
                        <div className="aspect-[16/10] overflow-hidden border-b border-white/5">
                          <img
                            src={post.image}
                            alt={`Emerge Automations Insight: ${post.title} - AI Automation for UK & USA`}
                            className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                            width="400"
                            height="250"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div className="p-6 flex flex-col flex-grow">
                          <h3 className="text-[17px] font-semibold text-white leading-[1.3] mb-3 line-clamp-2 group-hover:text-[color:var(--color-accent)] transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-sm text-zinc-400 leading-relaxed mb-5 line-clamp-3">
                            {post.excerpt}
                          </p>
                          <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between text-sm font-medium text-white">
                            <span>Read post</span>
                            <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] transition-colors" strokeWidth={1.75} />
                          </div>
                        </div>
                      </RevealCard>
                    </Link>
                  ))}
              </div>
            </RevealSection>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
